"use client";

import React, { useCallback, useEffect } from "react";

import { cn } from "@/lib/utils";
import {
  ref,
  query,
  orderByKey,
  limitToLast,
  onChildAdded,
} from "firebase/database";
import { firebaseDb, firebaseConfig } from "@/firebase/firebase-config";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { Button } from "@/components/ui/button";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowPathIcon,
} from "@heroicons/react/20/solid";
import { CameraIcon } from "@heroicons/react/24/outline";
import { CameraIcon as CameraIconSolid } from "@heroicons/react/24/solid";
import { Separator } from "@/components/ui/separator";
import { DateTimePicker } from "./date-time-picker";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { TimeLapseDialog } from "./time-lapse-dialog";
import { ImgPanZoom } from "./image-viewer";
import dayjs from "dayjs";
import { LogoCamsDropdown } from "./logo-cams-dropdown";
import Link from "next/link";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverAnchor } from "@/components/ui/popover";
import { toast } from "@/components/ui/use-toast";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { ToastClose } from "@radix-ui/react-toast";
import { Progress } from "@/components/ui/progress";

const bgClassName = "bg-black/50 border-white/30 backdrop-blur-2xl";

function FiveSecondCountdown({ className }: { className?: string }) {
  const [progress, setProgress] = React.useState(0);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setProgress((oldProgress) => {
        if (oldProgress >= 100) {
          clearInterval(interval);
          return 100;
        }
        return oldProgress + 2; // increment by 2 every 100ms to reach 100 in 5 seconds
      });
    }, 100); // update every 100ms
    return () => clearInterval(interval);
  }, []);

  return <Progress value={progress} className={className} />;
}

function BlurPillNav({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <nav
      className={cn(
        bgClassName,
        "p-3 px-3 md:px-4 border rounded-2xl z-10",
        className
      )}
    >
      {children}
    </nav>
  );
}

function useOptions({ cam }: { cam: string }) {
  const [options, setOptions] = React.useState<{
    blurEnabled: boolean;
    blurs: {
      height: number;
      width: number;
      x: number;
      y: number;
    }[];
    boxFolder: string;
    defaultZoomLevel: string;
    img: string;
    info: {
      img: string;
    };
    key: string;
    lat: string;
    long: string;
    mindate: string;
    name: string;
    otherCamKey: string;
    otherCamTitle: string;
    saturday: boolean;
    sunday: boolean;
    status: string;
    twoCams: boolean;
    v2: boolean;
    verkadaEmbedLinks: string[];
    videoS3Url: string;
    videoS3Urls: {
      [date: string]: string;
    };
    videoUrl?: string;
    projectUrl?: string;
    viewportMargin: boolean;
    viewportMarginBottom: string;
    viewportMarginLeft: string;
    viewportMarginRight: string;
    viewportMarginTop: string;
    weather: string;
  } | null>(null);
  const [error, setError] = React.useState<null | boolean>(null);

  React.useEffect(() => {
    async function getData() {
      try {
        const response = await fetch(
          firebaseConfig.databaseURL +
            `/projects/${cam.toUpperCase()}/options.json`,
          { next: { revalidate: 500 } }
        );
        const data = await response.json();
        setOptions(data);
        setError(false);
      } catch (err) {
        setError(true);
      }
    }
    getData();
    // get(ref(firebaseDb, `projects/${cam.toUpperCase()}/options`)).then((snap) =>
    //   setOptions(snap.val())
    // );
  }, [cam]);

  return { options, error };
}

function useList() {
  const [fullList, setFullList] = React.useState<any[]>([]);
  const [list, setList] = React.useState<any[]>([]);
  const [error, setError] = React.useState<null | boolean>(null);

  React.useEffect(() => {
    async function getData() {
      try {
        const response = await fetch(
          firebaseConfig.databaseURL + `/list.json`,
          { next: { revalidate: 500 } }
        );
        const data = await response.json();

        const fullList = Object.keys(data).map((key) => ({
          ...data[key],
          id: key,
        }));
        // Change the object to an array and filter down to status === 'active'
        const list = fullList.filter((item) => item.status === "active");

        setList(list);
        setFullList(fullList);
        setError(false);
      } catch (err) {
        setError(true);
      }
    }
    getData();
  }, []);

  return { list, error, fullList };
}

function useTimes({
  date,
  refreshTime,
  cam,
}: {
  date: string | null;
  refreshTime: number;
  cam: string;
}) {
  // Fetch the times for the date
  const [dateTimes, setDateTimes] = React.useState<string[]>([]);
  const [dateTimesLoading, setDateTimesLoading] = React.useState(true);

  React.useEffect(() => {
    if (!date || !cam) return;
    const getData = async () => {
      try {
        setDateTimesLoading(true);
        const path = `https://cs-cams.firebaseio.com/projects/${cam.toUpperCase()}/images/${dayjs(
          date
        ).format("YYYY/MM/DD")}.json?shallow=true`;
        const response = await fetch(path, { cache: "no-cache" });
        if (!response.ok) throw new Error(`Failed to fetch path data: ${path}`);
        const data = await response.json();

        if (!data) {
          setDateTimes([]);
          setDateTimesLoading(false);
          return;
        }

        const times = Object.keys(data).sort();
        setDateTimes(times);
        setDateTimesLoading(false);
      } catch (err) {
        setDateTimesLoading(false);
        console.error(err);
      }
    };
    getData();
  }, [date, refreshTime, cam]);

  return {
    dateTimes,
    dateTimesLoading,
  };
}

async function getLatestDay({ cam }: { cam: string }) {
  const fetchLastKey = async (key?: string) => {
    const path = `https://cs-cams.firebaseio.com/projects/${cam.toUpperCase()}/images${
      key ? `/${key}` : ""
    }.json?shallow=true`;
    const res = await fetch(path, { cache: "no-cache" });
    if (!res.ok) throw new Error(`Failed to fetch path data: ${path}`);
    const data = await res.json();
    // Retain any leading zeros, find the largest number in the object
    const keyWithTheHighestNumber = Object.keys(data).reduce(
      (a, b) => (parseInt(a) > parseInt(b) ? a : b),
      "-Infinity"
    );
    if (keyWithTheHighestNumber === "-Infinity") return null;
    return { keyWithTheHighestNumber, data };
  };
  const year = (await fetchLastKey())?.keyWithTheHighestNumber;
  if (!year) return null;
  const month = (await fetchLastKey(year))?.keyWithTheHighestNumber;
  if (!month) return null;
  const day = (await fetchLastKey(`${year}/${month}`))?.keyWithTheHighestNumber;
  if (!day) return null;
  const timeResponse = await fetchLastKey(`${year}/${month}/${day}`);
  const time = timeResponse?.keyWithTheHighestNumber;
  const dayData = timeResponse?.data;

  return { year, month, day, time, dayData };
}

function useLatestImage({
  cam,
  refreshTime,
  setDoesNewLatestDayExist,
  doesNewLatestDayExist,
}: {
  cam: string;
  refreshTime: number;
  setDoesNewLatestDayExist: React.Dispatch<React.SetStateAction<boolean>>;
  doesNewLatestDayExist: boolean;
}) {
  const [latestDate, setLatestDate] = React.useState<string | null>(null);
  const [latestTimes, setLatestTimes] = React.useState<string[]>([]);
  const [initialTimes, setInitialTimes] = React.useState<string[]>([]);
  const [initialDate, setInitialDate] = React.useState<string | null>(null);
  const [newImagesSinceLoad, setNewImagesSinceLoad] = React.useState<number>(0);
  const [error, setError] = React.useState<null | boolean>(null);
  const [isMounted, setIsMounted] = React.useState(false);
  const latestTimesRef = React.useRef(latestTimes); // Create a ref

  const getData = async () => {
    try {
      const response = await getLatestDay({ cam });
      if (!response) return null;

      const { year, month, day, time, dayData } = response;

      if (!time) return null;
      const fullDate = `${year}/${month}/${day}/${time}`;

      const times = Object.keys(dayData).sort();
      setInitialTimes(times);
      setInitialDate(fullDate);
      setLatestTimes(times);
      setNewImagesSinceLoad(0);
      setDoesNewLatestDayExist(false);

      // Listen for new keys on the latest day
      onChildAdded(
        query(
          ref(
            firebaseDb,
            `projects/${cam.toUpperCase()}/images/${year}/${month}/${day}`
          ),
          orderByKey(),
          limitToLast(1)
        ),
        (snapshot) => {
          const time = snapshot.key || "";
          const latestTimesValue = latestTimesRef.current;
          if (latestTimesValue?.includes(time) || times?.includes(time)) {
            return;
          }

          const newTimes = [...times, ...latestTimesValue, time];
          const deDupedTimes = [...new Set(newTimes)];
          // const newImagesFound = deDupedTimes.length - times.length;
          // setNewImagesSinceLoad(newImagesFound);
          setLatestTimes(deDupedTimes);

          // Replace the last time with the new time
          const newLatestTime = deDupedTimes[deDupedTimes.length - 1];
          const newFullDate = `${year}/${month}/${day}/${newLatestTime}`;
          setLatestDate(newFullDate);
        }
      );

      setLatestDate(fullDate);
      setError(false);
    } catch (err) {
      setError(true);
    }
  };

  React.useEffect(() => {
    setIsMounted(true);
  }, []);

  React.useEffect(() => {
    latestTimesRef.current = latestTimes; // Update the ref each time latestTimes changes
  }, [latestTimes]);

  React.useEffect(() => {
    if (!cam || !isMounted) return;
    getData();
  }, [cam, isMounted]);

  React.useEffect(() => {
    if (!latestDate) return;
    if (doesNewLatestDayExist) {
      getData();
    } else {
      setInitialTimes(latestTimes);
      setInitialDate(latestDate);
      setNewImagesSinceLoad(0);
    }
  }, [refreshTime]);

  return {
    latestDate,
    latestTimes,
    newImagesSinceLoad,
    setNewImagesSinceLoad,
    initialTimes,
    initialDate,
    error,
  };
}

function Page({ params }: { params: { cam: string } }) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams()!;
  const [datePopoverOpen, setDatePopoverOpen] = React.useState(false);
  const createQueryString = useCallback(
    (name: string, value: string) => {
      const params = new URLSearchParams(searchParams as any);
      params.set(name, value);
      return params.toString();
    },
    [searchParams]
  );
  const [refreshTime, setRefreshTime] = React.useState(
    Math.floor(Date.now() / 1000)
  );
  const { cam } = params;
  const [doesNewLatestDayExist, setDoesNewLatestDayExist] =
    React.useState(false);
  const {
    latestDate,
    latestTimes,
    newImagesSinceLoad,
    setNewImagesSinceLoad,
    initialTimes,
    initialDate,
    error: latestImageError,
  } = useLatestImage({
    cam,
    refreshTime,
    doesNewLatestDayExist,
    setDoesNewLatestDayExist,
  });
  const { options, error } = useOptions({ cam });
  const { list, fullList } = useList();
  const [keepMeLive, setKeepMeLive] = React.useState<string | null>("false");
  const [viewedImages, setViewedImages] = React.useState<string[]>([]);

  // Listen for window re-focus or switch between tabs like SWR does
  useEffect(() => {
    const onFocus = async () => {
      if (!cam || !latestDate) return;

      // console.log("Check for a new latest day", latestDate);
      const response = await getLatestDay({ cam });
      if (!response) return null;
      const { year, month, day } = response;
      const fullDate = `${year}/${month}/${day}`;
      const isNewLatestDayFound =
        dayjs(latestDate, "YYYY-MM-DD-HHmm").format("YYYY/MM/DD") !== fullDate;

      setDoesNewLatestDayExist(isNewLatestDayFound);
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [cam, latestDate, setDoesNewLatestDayExist]);

  // Check localstorage for a value called camsKeepMeLive and if it exists, set keepMeLive to true
  useEffect(() => {
    const isClient = typeof window !== "undefined";
    if (!isClient) return;
    setKeepMeLive(localStorage.getItem("camsKeepMeLive"));
  }, []);

  const cancelKeepMeLiveRef = React.useRef(false);
  useEffect(() => {
    if (
      !latestTimes ||
      (!doesNewLatestDayExist && !newImagesSinceLoad) ||
      keepMeLive !== "true"
    )
      return;

    toast({
      title: `New Image Available`,
      onOpenChange: (open: boolean) => {
        if (!open) {
          if (cancelKeepMeLiveRef.current) {
            console.log("latest image ignored");
            cancelKeepMeLiveRef.current = false;
          } else {
            router.push(`/cams/${cam}`);
            setRefreshTime(Math.floor(Date.now() / 1000));
          }
        }
      },
      action: (
        <ToastClose asChild>
          <Button
            size="sm"
            onClick={() => {
              cancelKeepMeLiveRef.current = true;
            }}
          >
            Cancel
          </Button>
        </ToastClose>
      ),
      description: (
        <div>
          <div>
            A new image is available and you have been switched to the latest
            image based on your settings.
          </div>
          {/* Countdown from 5 */}
          <FiveSecondCountdown className="absolute h-2 rounded-none bottom-0 left-0 right-0" />
        </div>
      ),
    });
  }, [latestTimes, newImagesSinceLoad, keepMeLive, doesNewLatestDayExist]);

  const loading =
    (!options && !error) || !latestDate || (!latestTimes && !latestImageError);

  const searchParamDate = searchParams.get("date") || initialDate || latestDate;
  const searchParamDayJsDate =
    !loading && searchParamDate
      ? dayjs(searchParamDate, "YYYY-MM-DD-HHmm").toDate()
      : null;
  const date = searchParamDayJsDate
    ? dayjs(searchParamDayJsDate).format("YYYY/MM/DD")
    : null;
  const time = dayjs(searchParamDayJsDate).format("HHmm");
  let { dateTimes = latestTimes, dateTimesLoading } = useTimes({
    date,
    refreshTime,
    cam,
  });

  const formattedLatestDate = dayjs(latestDate, "YYYY-MM-DD-HHmm").format(
    "YYYY/MM/DD"
  );
  const currentFullDate = searchParamDayJsDate
    ? dayjs(searchParamDayJsDate).format("YYYY-MM-DD-HHmm")
    : undefined;
  const isTodayLatestDate = formattedLatestDate === date;
  // Use the latest times when today is the latest date so it is realtime
  if (isTodayLatestDate) {
    dateTimes = latestTimes;
  }

  // Check that the time being viewed for the current date exists in the dateTimes array, if it does not, change the time to the nearest time in the array and display a toast message specifying that the image being viewed is not available and we are showing the nearest available image
  React.useEffect(() => {
    if (!dateTimes || dateTimesLoading || !time || !date) return;
    const readableDate = dayjs(date, "YYYY/MM/DD").format("MMM D, YYYY");

    if (!dateTimes?.length) {
      toast({
        title: `No Images available for ${readableDate}`,
        description: "Redirecting to the latest image",
      });
      router.push(`/cams/${cam}`);
      setRefreshTime(Math.floor(Date.now() / 1000));
    } else if (!dateTimes?.includes(time)) {
      const nearestTime = dateTimes.reduce((prev, curr) =>
        // @ts-ignore
        Math.abs(curr - time) < Math.abs(prev - time) ? curr : prev
      );
      const fullDate = `${dayjs(searchParamDayJsDate).format(
        "YYYY-MM-DD"
      )}-${nearestTime}`;

      const readableTime = dayjs(time, "HHmm").format("h:mm A");
      const readableNearestTime = dayjs(nearestTime, "HHmm").format("h:mm A");
      toast({
        title: `Image not available`,
        description: `An image at ${readableTime} on ${readableDate} is not available, showing the nearest available image at ${readableNearestTime}`,
      });
      router.replace(pathname + "?" + createQueryString("date", fullDate));
    }
  }, [dateTimes, time, dateTimesLoading]);

  React.useEffect(() => {
    import("lit-weather");
  }, []);

  const currentIndex = dateTimes?.indexOf(time) || 0;
  React.useEffect(() => {
    if (!date || !time || !isTodayLatestDate) return;

    const viewedImage = `${date}/${time}`;
    setViewedImages([...viewedImages, viewedImage]);
  }, [date, time, isTodayLatestDate]);

  React.useEffect(() => {
    if (!latestTimes?.length || !initialTimes.length || !formattedLatestDate) {
      return;
    }

    // This should equal the actual array of times since the initial load
    const timesSinceInitialLoad = latestTimes?.filter(
      (time) => !initialTimes.includes(time)
    );

    if (!timesSinceInitialLoad?.length && newImagesSinceLoad > 0) {
      setNewImagesSinceLoad(0);
    }

    if (!timesSinceInitialLoad?.length) return;

    // Check the times that have been viewed for the current date and compare them to the latest times, if there are any times that have not been viewed, set the newImagesSinceLoad to the number of times that have not been viewed
    const timesNotViewed = timesSinceInitialLoad?.filter(
      (time) => !viewedImages.includes(`${formattedLatestDate}/${time}`)
    );
    setNewImagesSinceLoad(timesNotViewed?.length || 0);
  }, [latestTimes, initialTimes, formattedLatestDate, viewedImages]);

  if (
    (options?.name || options?.key) &&
    (!options?.status || options?.status === "completed")
  ) {
    return (
      <div className="h-screen flex flex-col items-center justify-center p-12">
        <div className="text-center flex flex-col items-center justify-center">
          <LogoCamsDropdown project={cam} list={list} />
          <h2>This project has been completed</h2>

          <div className="mt-4 flex flex-col space-y-2">
            {options?.videoUrl ? (
              <a
                className="text-sm underline font-medium "
                href={options.videoUrl}
              >
                Timelapse Video &rarr;
              </a>
            ) : null}
            {options?.projectUrl ? (
              <a
                className="text-sm underline font-medium mt-6"
                href={options.projectUrl}
              >
                Project Page &rarr;
              </a>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  if (error) return <div className="p-10">Error loading camera</div>;

  if (!options && error === false) {
    return <div className="p-10">Camera Not found</div>;
  }

  function goToPreviousImage() {
    if (!searchParams) return;
    const currentTimeIndex = dateTimes?.indexOf(time);
    const previousTime = dateTimes?.[currentTimeIndex - 1];
    const fullDate = `${dayjs(searchParamDayJsDate).format(
      "YYYY-MM-DD"
    )}-${previousTime}`;
    router.push(pathname + "?" + createQueryString("date", fullDate));
  }

  function goToNextImage() {
    if (!searchParams) return;
    const currentTimeIndex = dateTimes?.indexOf(time);
    const nextTime = dateTimes?.[currentTimeIndex + 1];
    if (!nextTime) return;
    const fullDate = `${dayjs(searchParamDayJsDate).format(
      "YYYY-MM-DD"
    )}-${nextTime}`;

    router.push(pathname + "?" + createQueryString("date", fullDate));
  }

  function computeSrc({
    cam,
    date,
    time,
  }: {
    cam: string;
    date?: string | null;
    time: string;
  }) {
    if (!date) return undefined;

    // This is for a special case where the camera was renamed and the images were moved to a new bucket,
    // once renowcup is no longer needed, this can be removed
    if (
      cam.toUpperCase() === "RENOWNCUP" &&
      Number(date.replace(/\//g, "")) < 20230804
    ) {
      return `https://s3-us-west-1.amazonaws.com/cs-cams-archive/${date}/${time}/output.dzi`;
    }

    return `https://clark-sullivan-cams.s3.us-west-1.amazonaws.com/projects/${cam.toUpperCase()}/images/${date}/${time}/output.dzi`;
  }

  const listItem = fullList?.find((item) => {
    return (
      item.key === cam ||
      item.key === cam.toUpperCase() ||
      item.key === cam.toLowerCase()
    );
  });
  return (
    <div key={cam} className="dark">
      <header>
        <Popover open={datePopoverOpen} onOpenChange={setDatePopoverOpen}>
          <BlurPillNav className="fixed border-0 md:border right-0 md:right-auto left-0 md:left-[50vw] rounded-none md:rounded-2xl top-0 md:top-4 mx-auto pb-2 md:-translate-x-1/2">
            <div className="flex items-center space-x-0.5">
              {/* <Logo className="h-7 w-7" /> */}
              <LogoCamsDropdown project={cam} list={list} />

              <h1 className="text-ellipsis truncate text-white text-base font-bold">
                {options?.name ? (
                  options?.name
                ) : (
                  <Skeleton className="w-[70%] h-[20px] bg-white/20 rounded-full justify-center items-center" />
                )}
              </h1>

              {options?.twoCams &&
              options?.twoCams &&
              options?.otherCamTitle ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      asChild
                      size="sm"
                      variant={"link"}
                      className="px-2 relative group sm:hidden"
                    >
                      <Link
                        className="capitalize group"
                        href={`/cams/${options?.otherCamKey.toLowerCase()}?date=${currentFullDate}`}
                      >
                        <CameraIcon className="h-5 w-5 group-hover:hidden" />
                        <CameraIconSolid className="h-5 w-5 hidden group-hover:inline-flex" />
                      </Link>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent align="center" side="bottom">
                    <div>
                      View{" "}
                      <span className="capitalize">
                        {options.otherCamTitle.toLowerCase()}
                      </span>
                    </div>
                  </TooltipContent>
                </Tooltip>
              ) : null}
            </div>

            <PopoverAnchor>
              <div className="flex items-center">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      disabled={loading || currentIndex === 0}
                      className="rounded-r-none items-center flex px-2 pl-0 -ml-2"
                      size="sm"
                      variant={"link"}
                      onClick={goToPreviousImage}
                    >
                      <ChevronLeftIcon className="h-5 w-5" />
                      {loading ? (
                        <Skeleton className="w-[35px] h-[20px] bg-white/20 rounded-full justify-center items-center" />
                      ) : (
                        currentIndex + 1 || dateTimes?.length
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <p>Previous Image</p>
                  </TooltipContent>
                </Tooltip>

                <Separator className="h-4 bg-white/50" orientation="vertical" />

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      className="rounded-l-none px-2 pr-0 items-center flex"
                      size="sm"
                      variant={"link"}
                      disabled={
                        loading || currentIndex === dateTimes?.length - 1
                      }
                      onClick={goToNextImage}
                    >
                      {loading ? (
                        <Skeleton className="w-[35px] h-[20px] bg-white/20 rounded-full justify-center items-center" />
                      ) : (
                        dateTimes?.length
                      )}
                      <ChevronRightIcon className="h-5 w-5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <p>Next Image</p>
                  </TooltipContent>
                </Tooltip>

                <DateTimePicker
                  loading={loading}
                  date={searchParamDayJsDate}
                  saturday={options?.saturday}
                  sunday={options?.sunday}
                  time={time}
                  mindate={options?.mindate}
                  open={datePopoverOpen}
                  dateTimes={dateTimes}
                  keepMeLive={keepMeLive}
                  onKeepMeLiveChange={(value: string) => {
                    setKeepMeLive(value);
                  }}
                  setDate={(value) => {
                    const fullDate = `${dayjs(value).format(
                      "YYYY-MM-DD-HHmm"
                    )}`;
                    router.push(
                      `/cams/${cam}` + "?" + createQueryString("date", fullDate)
                    );
                  }}
                />

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      onClick={() => {
                        router.push(`/cams/${cam}`);
                        setRefreshTime(Math.floor(Date.now() / 1000));
                        // router.refresh();
                      }}
                      size="sm"
                      variant={"link"}
                      className="px-2 relative group"
                    >
                      <ArrowPathIcon className="h-5 w-5 group-hover:rotate-90 transition-transform" />
                      {newImagesSinceLoad || doesNewLatestDayExist ? (
                        <Badge className="text-xs absolute flex flex-col items-center bg-accent-500 text-white -top-2.5 -right-1 opacity-90 px-1 pointer-events-none">
                          {doesNewLatestDayExist ? "1+" : newImagesSinceLoad}
                        </Badge>
                      ) : null}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent align="center" side="bottom">
                    <div>
                      View Latest Image
                      {newImagesSinceLoad > 0 ? (
                        <Badge className="ml-2">{newImagesSinceLoad}</Badge>
                      ) : null}
                    </div>
                  </TooltipContent>
                </Tooltip>

                <TimeLapseDialog
                  image={options?.img}
                  cam={cam}
                  mindate={options?.mindate}
                  lastFullTimeLapseGeneration={
                    listItem?.lastFullTimeLapseGeneration
                  }
                  videos={options?.videoS3Urls}
                />

                {options?.twoCams &&
                options?.otherCamTitle &&
                options?.otherCamKey ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        asChild
                        size="sm"
                        variant={"link"}
                        className="px-2 relative group hidden sm:inline-flex"
                      >
                        <Link
                          className="capitalize group"
                          href={`/cams/${options?.otherCamKey.toLowerCase()}?date=${currentFullDate}`}
                        >
                          <CameraIcon className="h-5 w-5 mr-2 group-hover:hidden" />
                          <CameraIconSolid className="h-5 w-5 mr-2 hidden group-hover:inline-flex" />
                          {options?.otherCamTitle?.toLowerCase()}
                        </Link>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent align="center" side="bottom">
                      <div>
                        View{" "}
                        <span className="capitalize">
                          {options?.otherCamTitle?.toLowerCase()}
                        </span>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                ) : null}
              </div>
            </PopoverAnchor>
          </BlurPillNav>
        </Popover>
      </header>

      {options?.weather ? (
        <Dialog>
          <DialogTrigger asChild>
            <button
              className={cn(
                "left-6 z-10 hidden md:flex p-2 rounded-lg bottom-4 fixed border",
                bgClassName
              )}
            >
              {loading ? (
                <Skeleton className="w-[55px] h-[28px] bg-white/20 rounded-full justify-center items-center" />
              ) : (
                <lit-weather
                  variant="preview"
                  query={options?.weather}
                  token="d3b40ca1d117daaa623ac3012c8ae6bd"
                />
              )}
            </button>
          </DialogTrigger>

          <DialogContent className="sm:max-w-[500px]">
            <lit-weather
              query={options?.weather}
              token="d3b40ca1d117daaa623ac3012c8ae6bd"
            />
          </DialogContent>
        </Dialog>
      ) : null}

      {loading || !latestDate ? (
        <div className="fixed inset-0 m-auto z-10 flex-col flex items-center justify-center">
          Loading...
        </div>
      ) : (
        <ImgPanZoom
          src={
            computeSrc({
              cam,
              date,
              time,
            }) as string
          }
          className="fixed z-1 inset-0 bg-center bg-black"
          blurs={
            options?.blurEnabled && options?.blurs ? options?.blurs : undefined
          }
          defaultZoomLevel={
            options?.defaultZoomLevel
              ? Number(options?.defaultZoomLevel)
              : undefined
          }
          project={cam}
          bgClassName={bgClassName}
          viewportMargins={
            options?.viewportMargin
              ? {
                  top: Number(options.viewportMarginTop),
                  right: Number(options.viewportMarginRight),
                  bottom: Number(options.viewportMarginBottom),
                  left: Number(options.viewportMarginLeft),
                }
              : undefined
          }
        />
      )}
    </div>
  );
}

export default Page;
